import { Button, Menu, MenuItem, Paper, Select, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material'
import React, { useEffect, useState } from 'react'
import { timeToSeconds } from '../../../utils/generalFunctions';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import BootstrapTooltip from '../../../components/tooltip';
import { logoBase64 } from '../../../assets/images/dashboard/logo_base64';
import { leptonCheckLogoBase64 } from '../../../assets/images/dashboard/leptonCheck-logo_base64';

const ScheduleTab = ({ devices, userSchedules, selectedSchedule, vehicleSchedules, setSelectedSchedule, defaultEventRows, selectStyles }) => {
    const [isTransposedView, setIsTransposedView] = useState(false);
    const exportColors = {
        primary: { argb: '0074FF', rgb: [0, 116, 255], css: 'var(--color-primary)' },
        dark: { argb: '1F2933', rgb: [31, 41, 51] },
        danger: { argb: 'D32F2F', rgb: [211, 47, 47], css: 'var(--color-danger)' },
        warning: { argb: 'ED6C02', rgb: [237, 108, 2], css: 'var(--color-warning)' },
        success: { argb: '2E7D32', rgb: [46, 125, 50], css: 'var(--color-success)' }
    };

    const formatTime = (time) => {
        if (time === null || time === undefined) return ""; // Mostrar cadena vacía si el tiempo es null
        const hours = typeof time === 'string' ? time.split(':')[0] : Math.floor(time / 3600);
        const minutes = typeof time === 'string' ? time.split(':')[1] : Math.floor((time % 3600) / 60);
        const seconds = typeof time === 'string' ? time.split(':')[2] : Math.floor(time % 60);
        return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
    };

    const getStatusColor = (status, difference) => {
        if (status === 'Omitido' || status === 'Atrasado') return exportColors.danger;
        if (status === 'Adelantado') return exportColors.warning;
        if (difference) return exportColors.success;
        return exportColors.dark;
    };

    const statusTotalRows = [
        { status: 'Atrasado', label: 'Atrasado', color: exportColors.danger },
        { status: 'En tiempo', label: 'En tiempo', color: exportColors.success },
        { status: 'Adelantado', label: 'Adelantado', color: exportColors.warning }
    ];

    const getStatusTotals = (points) => {
        return points.reduce((totals, point) => {
            if (point?.status === 'Atrasado') {
                totals['Atrasado'].count += 1;
                totals['Atrasado'].seconds += parseDifferenceSeconds(point.difference);
            }
            if (point?.status === 'En tiempo') {
                totals['En tiempo'].count += 1;
                totals['En tiempo'].seconds += parseDifferenceSeconds(point.difference);
            }
            if (point?.status === 'Adelantado') {
                totals['Adelantado'].count += 1;
                totals['Adelantado'].seconds += parseDifferenceSeconds(point.difference);
            }
            return totals;
        }, {
            'Atrasado': { count: 0, seconds: 0 },
            'En tiempo': { count: 0, seconds: 0 },
            'Adelantado': { count: 0, seconds: 0 }
        });
    };

    const parseDifferenceSeconds = (difference) => {
        if (!difference || typeof difference !== 'string') return 0;

        const sign = difference.trim().startsWith('-') ? -1 : 1;
        const minutesMatch = difference.match(/(\d+)\s*min/);
        const secondsMatch = difference.match(/(\d+)\s*seg/);
        const minutes = minutesMatch ? Number(minutesMatch[1]) : 0;
        const seconds = secondsMatch ? Number(secondsMatch[1]) : 0;

        return sign * ((minutes * 60) + seconds);
    };

    const formatAccumulatedTime = (totalSeconds) => {
        const sign = totalSeconds < 0 ? '-' : totalSeconds > 0 ? '+' : '';
        const absoluteSeconds = Math.abs(totalSeconds);
        const hours = Math.floor(absoluteSeconds / 3600);
        const minutes = Math.floor((absoluteSeconds % 3600) / 60);
        const seconds = absoluteSeconds % 60;

        if (hours > 0) {
            return `${sign} ${hours} h ${minutes} min ${seconds} seg`.trim();
        }

        return `${sign} ${minutes} min ${seconds} seg`.trim();
    };

    const getStatusTotalExportData = (status, total = null) => ({
        'data-status-total': status,
        'data-status-total-count': total?.count ?? '',
        'data-status-total-time': total ? formatAccumulatedTime(total.seconds || 0) : ''
    });

    const getPointExportData = (point) => {
        const scheduledTime = point.time === null ? '' : formatTime(point.time);

        return {
            'data-point-cell': 'true',
            'data-scheduled-time': scheduledTime,
            'data-event-time': point.eventTime || '',
            'data-status': point.status || '',
            'data-difference': point.difference || ''
        };
    };

    const getStatusTotalDataset = (rawCell) => {
        if (!rawCell?.dataset?.statusTotal) return null;

        const statusData = statusTotalRows.find(row => row.status === rawCell.dataset.statusTotal);

        if (!statusData) return null;

        return {
            ...statusData,
            count: rawCell.dataset.statusTotalCount || '',
            time: rawCell.dataset.statusTotalTime || ''
        };
    };

    const getPointDataset = (rawCell) => {
        if (!rawCell?.dataset || rawCell.dataset.pointCell !== 'true') return null;

        return {
            scheduledTime: rawCell.dataset.scheduledTime || '',
            eventTime: rawCell.dataset.eventTime || '',
            status: rawCell.dataset.status || '',
            difference: rawCell.dataset.difference || ''
        };
    };

    const drawCenteredSegments = (doc, segments, centerX, y) => {
        const visibleSegments = segments.filter(segment => segment.text);
        const totalWidth = visibleSegments.reduce((width, segment) => width + doc.getTextWidth(segment.text), 0);
        let currentX = centerX - (totalWidth / 2);

        visibleSegments.forEach(segment => {
            doc.setTextColor(...segment.color);
            doc.text(segment.text, currentX, y);
            currentX += doc.getTextWidth(segment.text);
        });
    };

    const drawPdfPointCell = (doc, data) => {
        const pointData = getPointDataset(data.cell.raw);
        if (!pointData) return;

        const statusText = pointData.status === 'Omitido' ? pointData.status : pointData.difference;
        const statusColor = getStatusColor(pointData.status, pointData.difference).rgb;
        const centerX = data.cell.x + (data.cell.width / 2);
        const centerY = data.cell.y + (data.cell.height / 2);
        const lineGap = statusText ? 5 : 0;

        doc.setFontSize(data.cell.styles.fontSize || 7);
        doc.setFont(undefined, statusText ? 'normal' : data.cell.styles.fontStyle || 'normal');

        drawCenteredSegments(doc, [
            { text: pointData.scheduledTime, color: exportColors.dark.rgb },
            { text: pointData.eventTime ? ' - ' : '', color: exportColors.dark.rgb },
            { text: pointData.eventTime, color: exportColors.primary.rgb }
        ], centerX, centerY - lineGap);

        if (statusText) {
            doc.setFont(undefined, 'bold');
            drawCenteredSegments(doc, [
                { text: statusText, color: statusColor }
            ], centerX, centerY + 6);
            doc.setFont(undefined, 'normal');
        }
    };

    const configuredSchedules = Object.entries(vehicleSchedules)
        .map(([vehicleId, scheduleName]) => {
            const schedule = userSchedules.find(
                s => s.name === scheduleName
            );

            const vehicle = devices.find(
                d => String(d.id) === String(vehicleId)
            );

            if (!schedule || !vehicle) return null;

            return {
                ...schedule,
                configuredVehicleId: vehicle.id,
                configuredVehicleLabel: vehicle.label
            };
        })
        .filter(Boolean);

    useEffect(() => {
        if (configuredSchedules.length > 0 && !selectedSchedule?.name) {
            setSelectedSchedule(configuredSchedules[0]);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [vehicleSchedules, userSchedules]);

    const normalizeLabel = (text) => {
        return text
            ?.replace(' Ida', '')
            ?.replace(' Vuelta', '')
            ?.trim()
            ?.toLowerCase();
    };

    const checkpointLabelsMatch = (eventCheckpoint, scheduleCheckpoint) => {
        const isDirectional =
            scheduleCheckpoint.includes('Ida') ||
            scheduleCheckpoint.includes('Vuelta');

        if (isDirectional) {
            return eventCheckpoint?.trim()?.toLowerCase() === scheduleCheckpoint.trim().toLowerCase();
        }

        return normalizeLabel(eventCheckpoint) === normalizeLabel(scheduleCheckpoint);
    };

    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    const handleClickDownload = (event) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };

    const buildPdfTableChunks = (table, maxDataColumns) => {
        const headerCells = Array.from(table.rows[1]?.cells || []);
        const firstHeaderText = headerCells[0]?.innerText?.trim();
        const hasLeadingColumn = firstHeaderText === 'Punto de control' || firstHeaderText === 'Vuelta';
        const dataColumnCount = hasLeadingColumn ? headerCells.length - 1 : headerCells.length;

        if (dataColumnCount <= maxDataColumns) {
            return [{ table, hasLeadingColumn, columnCount: headerCells.length }];
        }

        const chunks = [];
        const totalChunks = Math.ceil(dataColumnCount / maxDataColumns);

        for (let start = 0; start < dataColumnCount; start += maxDataColumns) {
            const end = Math.min(start + maxDataColumns, dataColumnCount);
            const chunkTable = document.createElement('table');
            chunkTable.className = table.className;

            Array.from(table.rows).forEach((sourceRow, rowIndex) => {
                const row = document.createElement('tr');
                const cells = Array.from(sourceRow.cells);

                if (rowIndex === 0) {
                    const titleCell = cells[0].cloneNode(true);
                    titleCell.colSpan = (hasLeadingColumn ? 1 : 0) + (end - start);

                    if (totalChunks > 1) {
                        titleCell.textContent = `${titleCell.textContent} - Columnas ${start + 1}-${end}`;
                    }

                    row.appendChild(titleCell);
                } else {
                    const selectedCells = hasLeadingColumn
                        ? [cells[0], ...cells.slice(start + 1, end + 1)]
                        : cells.slice(start, end);

                    selectedCells.forEach((cell) => {
                        row.appendChild(cell.cloneNode(true));
                    });
                }

                chunkTable.appendChild(row);
            });

            chunks.push({
                table: chunkTable,
                hasLeadingColumn,
                columnCount: (hasLeadingColumn ? 1 : 0) + (end - start)
            });
        }

        return chunks;
    };

    const handleExportPDF = () => {
        const doc = new jsPDF({
            orientation: 'landscape',
            unit: 'pt',
            format: 'A4'
        });

        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const headerHeight = 55;
        const logoWidth = 100;
        const logoHeight = 35;
        const marginX = 40;
        const footerMarginY = 18;
        const reportName = "Reporte de flotas por horario";

        // Para paginación real
        const footerTexts = [];

        // Exporta TODAS las tablas visibles (cada tabla por fecha)
        const tables = Array.from(document.querySelectorAll('.schedule_table'))
            .flatMap((table) => buildPdfTableChunks(table, isTransposedView ? 8 : 9));
        let startY = headerHeight;
        let isFirstTableChunk = true;
        tables.forEach((tableChunk) => {
            const availableWidth = pageWidth - (marginX * 2);
            const leadingColumnWidth = tableChunk.hasLeadingColumn ? 110 : 0;
            const dataColumnCount = tableChunk.hasLeadingColumn ? tableChunk.columnCount - 1 : tableChunk.columnCount;
            const dataColumnWidth = (availableWidth - leadingColumnWidth) / Math.max(dataColumnCount, 1);
            const columnStyles = {};

            for (let colIndex = 0; colIndex < tableChunk.columnCount; colIndex++) {
                columnStyles[colIndex] = {
                    cellWidth: tableChunk.hasLeadingColumn && colIndex === 0
                        ? leadingColumnWidth
                        : dataColumnWidth
                };
            }

            if (!isFirstTableChunk) {
                doc.addPage();
                startY = headerHeight;
            }

            autoTable(doc, {
                html: tableChunk.table,
                startY,
                margin: { top: headerHeight, right: marginX, bottom: 35, left: marginX },
                tableWidth: 'wrap',
                styles: {
                    fontSize: 7,
                    cellPadding: 2,
                    overflow: 'linebreak',
                    halign: 'center',
                    valign: 'middle',
                    lineColor: [217, 226, 236],
                    lineWidth: 0.25
                },
                headStyles: {
                    fillColor: exportColors.primary.rgb,
                    textColor: [255, 255, 255],
                    fontStyle: 'bold',
                    minCellHeight: 18
                },
                columnStyles,
                didParseCell: function (data) {
                    const pointData = getPointDataset(data.cell.raw);
                    const totalData = getStatusTotalDataset(data.cell.raw);

                    if (pointData) {
                        data.cell.text = [''];
                        data.cell.styles.minCellHeight = 28;
                        data.cell.styles.valign = 'middle';
                    }

                    if (totalData) {
                        if (totalData.count || totalData.time) {
                            data.cell.text = [totalData.count, totalData.time].filter(Boolean);
                        }
                        data.cell.styles.textColor = totalData.color.rgb;
                        data.cell.styles.fontStyle = 'bold';
                        data.cell.styles.minCellHeight = 24;
                    }
                },
                didDrawCell: function (data) {
                    drawPdfPointCell(doc, data);
                },
                didDrawPage: function (data) {
                    // --- Header ---
                    if (logoBase64) {
                        doc.addImage(logoBase64, 'PNG', marginX, 12, logoWidth, logoHeight);
                    }
                    // Dibuja el segundo logo (header derecho)
                    const headerImgWidth = 165;  // ajusta según tamaño de tu nueva imagen
                    const headerImgHeight = 35;  // ajusta según tamaño de tu nueva imagen
                    if (leptonCheckLogoBase64) {
                        doc.addImage(
                            leptonCheckLogoBase64,
                            'PNG',
                            pageWidth - marginX - headerImgWidth,
                            12,
                            headerImgWidth,
                            headerImgHeight
                        );
                    }

                    // --- Footer marcador temporal ---
                    const pageNumber = doc.internal.getCurrentPageInfo().pageNumber;
                    footerTexts.push({
                        page: pageNumber,
                        y: pageHeight - footerMarginY
                    });

                    // Pie de página: nombre del reporte (izquierda), paginación (derecha)
                    doc.setFontSize(10);
                    doc.setTextColor(100);
                    doc.text(reportName, marginX, pageHeight - footerMarginY, { align: 'left' });
                    doc.text(`Página ${pageNumber} de {total}`, pageWidth - marginX, pageHeight - footerMarginY, { align: 'right' });
                }
            });
            // Si hay varias tablas, ajusta el nuevo startY (esto es importante si tienes varias tablas largas)
            startY = doc.lastAutoTable.finalY + 20;
            isFirstTableChunk = false;
        });

        // Corrige el total de páginas en los pies
        const totalPages = doc.internal.getNumberOfPages();
        for (let i = 1; i <= totalPages; i++) {
            doc.setPage(i);
            const footer = footerTexts[i - 1];
            if (footer) {
                doc.setFillColor(255, 255, 255);
                doc.rect(marginX - 5, footer.y - 10, pageWidth - 2 * marginX + 10, 16, "F");
                doc.setFontSize(10);
                doc.setTextColor(100);
                doc.text(reportName, marginX, footer.y, { align: 'left' });
                doc.text(`Página ${i} de ${totalPages}`, pageWidth - marginX, footer.y, { align: 'right' });
            }
        }

        doc.save('Reporte de flotas por horario.pdf');
    };


    const handleExportExcel = async () => {
        const tables = document.querySelectorAll('.schedule_table');
        const workbook = new ExcelJS.Workbook();
        workbook.creator = 'Lepton Check';
        workbook.created = new Date();

        tables.forEach((table, idx) => {
            const worksheet = workbook.addWorksheet(`Tabla${idx + 1}`, {
                views: [{ state: 'frozen', ySplit: 2 }]
            });

            Array.from(table.rows).forEach((tableRow, rowIndex) => {
                const worksheetRow = worksheet.getRow(rowIndex + 1);
                let colIndex = 1;

                Array.from(tableRow.cells).forEach((tableCell) => {
                    const cell = worksheetRow.getCell(colIndex);
                    const cellText = tableCell.innerText.replace(/\n+/g, '\n').trim();
                    const colSpan = tableCell.colSpan || 1;
                    const pointData = getPointDataset(tableCell);
                    const totalData = getStatusTotalDataset(tableCell);

                    cell.value = cellText;
                    cell.alignment = {
                        horizontal: 'center',
                        vertical: 'middle',
                        wrapText: true
                    };
                    cell.border = {
                        top: { style: 'thin', color: { argb: 'D9E2EC' } },
                        left: { style: 'thin', color: { argb: 'D9E2EC' } },
                        bottom: { style: 'thin', color: { argb: 'D9E2EC' } },
                        right: { style: 'thin', color: { argb: 'D9E2EC' } }
                    };

                    if (rowIndex === 0) {
                        cell.font = { bold: true, size: 14, color: { argb: 'FFFFFF' } };
                        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '0074FF' } };
                    } else if (rowIndex === 1) {
                        cell.font = { bold: true, size: 11, color: { argb: 'FFFFFF' } };
                        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '0074FF' } };
                    } else {
                        const isEvenRow = rowIndex % 2 === 0;
                        cell.font = { size: 10, color: { argb: exportColors.dark.argb } };
                        cell.fill = {
                            type: 'pattern',
                            pattern: 'solid',
                            fgColor: { argb: isEvenRow ? 'E6F0FF' : 'FFFFFF' }
                        };

                        if (pointData) {
                            const statusText = pointData.status === 'Omitido' ? pointData.status : pointData.difference;
                            const statusColor = getStatusColor(pointData.status, pointData.difference);
                            const richText = [];

                            if (pointData.scheduledTime) {
                                richText.push({
                                    text: pointData.scheduledTime,
                                    font: { size: 10, color: { argb: exportColors.dark.argb } }
                                });
                            }

                            if (pointData.eventTime) {
                                richText.push({
                                    text: ' - ',
                                    font: { size: 10, color: { argb: exportColors.dark.argb } }
                                });
                                richText.push({
                                    text: pointData.eventTime,
                                    font: { size: 10, color: { argb: exportColors.primary.argb } }
                                });
                            }

                            if (statusText) {
                                richText.push({
                                    text: `\n${statusText}`,
                                    font: { size: 10, bold: true, color: { argb: statusColor.argb } }
                                });
                            }

                            cell.value = richText.length > 0 ? { richText } : '';
                        } else if (totalData) {
                            if (totalData.count || totalData.time) {
                                cell.value = [totalData.count, totalData.time].filter(Boolean).join('\n');
                            }
                            cell.font = { size: 10, bold: true, color: { argb: totalData.color.argb } };
                            worksheetRow.height = Math.max(worksheetRow.height || 0, 42);
                        }
                    }

                    if (colSpan > 1) {
                        worksheet.mergeCells(rowIndex + 1, colIndex, rowIndex + 1, colIndex + colSpan - 1);
                    }

                    colIndex += colSpan;
                });
            });

            worksheet.eachRow((row) => {
                row.height = row.height || (row.number <= 2 ? 24 : 34);
            });

            worksheet.columns.forEach((column) => {
                let maxLength = 12;

                column.eachCell({ includeEmpty: true }, (cell) => {
                    const text = cell.value?.toString() || '';
                    const longestLine = text.split('\n').reduce((max, line) => Math.max(max, line.length), 0);
                    maxLength = Math.max(maxLength, longestLine);
                });

                column.width = Math.min(Math.max(maxLength + 2, 14), 28);
            });
        });

        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer]), 'Reporte de flotas por horario.xlsx');
    };

    const renderPointCellContent = (point) => (
        <>
            <span style={{ display: 'block', whiteSpace: 'nowrap' }}>
                {point.time === null ? '' : formatTime(point.time)}
                {point.eventTime ? ' - ' : ''}
                <span style={{ color: 'var(--color-primary)' }}>{point.eventTime}</span>
            </span>
            {point.status === 'Omitido'
                ? <span style={{ color: 'var(--color-danger)' }}>Omitido</span>
                : <span style={{
                    color: point.status === 'Atrasado' ? 'var(--color-danger)' :
                        point.status === 'Adelantado' ? 'var(--color-warning)' :
                            'var(--color-success)'
                }}>{point.difference || ''}</span>}
        </>
    );

    const renderStatusTotalContent = (total) => (
        <>
            <span style={{ display: 'block', whiteSpace: 'nowrap', color: 'inherit', fontWeight: 700 }}>
                {total?.count || 0}
            </span>
            <span style={{ display: 'block', whiteSpace: 'nowrap', fontSize: '0.65rem', color: 'inherit', fontWeight: 700 }}>
                {formatAccumulatedTime(total?.seconds || 0)}
            </span>
        </>
    );

    const renderComparisonTable = () => {

        if (!selectedSchedule || !selectedSchedule.turns) {
            return (
                <div style={{ margin: 'auto 0', textAlign: 'center' }}>
                    <span style={{ fontStyle: 'italic' }}>
                        Selecciona un horario.
                    </span>
                </div>
            );
        }
        const vehicleLabel = selectedSchedule.configuredVehicleLabel;
        const allTurns = selectedSchedule.turns;
        const tables = [];

        const vehicleId = Object.entries(vehicleSchedules)
            .find(([_, scheduleName]) =>
                scheduleName === selectedSchedule.name
            )?.[0];

        const filteredEvents = defaultEventRows
            .filter(event =>
                String(event.trackerId) === String(vehicleId) &&
                event.turnNumber !== '-'
            )
            .sort((a, b) =>
                new Date(a.timeDate) - new Date(b.timeDate)
            );
            
        const eventsByDate = new Map();
        for (const event of filteredEvents) {
            const dateKey = event.time.split(' - ')[1];
            if (!eventsByDate.has(dateKey)) eventsByDate.set(dateKey, []);
            eventsByDate.get(dateKey).push(event);
        }

        for (const [dateKey, eventsOfDay] of eventsByDate.entries()) {
            // Copia profunda de los turnos (para no modificar el original)
            const turnsCopy = JSON.parse(JSON.stringify(allTurns));
            const processedTurn = turnsCopy.map(turn =>
                turn.map(cp => ({
                    ...cp,
                    eventTime: '',
                    status: '',
                    difference: ''
                }))
            );

            // --- Matching por checkpoint ---
            const usedEvents = new Set();

            for (let t = 0; t < processedTurn.length; t++) {
                for (let p = 0; p < processedTurn[t].length; p++) {
                    const checkpoint = processedTurn[t][p];

                    // Sólo para checkpoints con hora válida
                    if (checkpoint.time === null) continue;

                    // Busca evento NO usado, misma label, misma vuelta, mejor diferencia
                    let bestEventIdx = null;
                    let bestDiff = 600;

                    for (let e = 0; e < eventsOfDay.length; e++) {
                        if (usedEvents.has(e)) continue;
                        const event = eventsOfDay[e];
                        // Checkpoint label y vuelta deben coincidir
                        const sameTurn = Number(event.turnNumber) === (t + 1);
                        const sameCheckpointIndex =
                            event.checkpointIndex != null &&
                            Number(event.checkpointIndex) === p;

                        let labelsMatch = checkpointLabelsMatch(
                            event.checkpoint,
                            checkpoint.label
                        );

                        if (checkpoint.__legacyCommonMatching) {

                            // Comparación normal para checkpoints comunes
                            const normalize = (text) =>
                                text
                                    .replace(' Ida', '')
                                    .replace(' Vuelta', '')
                                    .trim();

                            labelsMatch =
                                normalize(event.checkpoint) ===
                                normalize(checkpoint.label);
                        }

                        if (
                            sameTurn &&
                            sameCheckpointIndex
                        ) {

                            bestEventIdx = e;
                            break;
                        }
                    }

                    if (bestEventIdx !== null) {
                        const event = eventsOfDay[bestEventIdx];
                        checkpoint.eventTime = event.time.split(' - ')[0];
                        checkpoint.status = event.status;
                        checkpoint.difference = event.difference;

                        usedEvents.add(bestEventIdx);
                    } else {
                        checkpoint.eventTime = '';
                        checkpoint.status = '';
                        checkpoint.difference = '';
                    }
                }
            }

            // Lógica para “Omitido” (igual a tu segunda pasada si lo deseas)
            processedTurn.forEach(() => {
                let lastMatchGlobal = { turnIdx: -1, pointIdx: -1 };

                for (let t = 0; t < processedTurn.length; t++) {
                    for (let p = 0; p < processedTurn[t].length; p++) {
                        const et = processedTurn[t][p].eventTime;
                        const isRealEvent = et && et !== '' && et !== '--:--:--';
                        if (isRealEvent) {
                            lastMatchGlobal = { turnIdx: t, pointIdx: p };
                        }
                    }
                }

                // Segunda pasada para aplicar "Omitido" global
                for (let t = 0; t < processedTurn.length; t++) {
                    for (let p = 0; p < processedTurn[t].length; p++) {
                        const point = processedTurn[t][p];

                        const isBeforeLastMatch =
                            (t < lastMatchGlobal.turnIdx) ||
                            (t === lastMatchGlobal.turnIdx && p < lastMatchGlobal.pointIdx);

                        if (isBeforeLastMatch && !point.eventTime && point.time !== null) {
                            point.eventTime = '--:--:--';
                            point.status = 'Omitido';
                            point.difference = '';
                        } else if (!point.eventTime) {
                            point.eventTime = '';
                            point.status = '';
                            point.difference = '';
                        }
                    }
                }
            });

            tables.push({
                rows: processedTurn,
                dates: dateKey
            });
        }
        // console.log(tables)
        if (tables.length === 0) {
            return (
                <div style={{ margin: 'auto 0', textAlign: 'center' }}>
                    <span style={{ fontStyle: 'italic' }}>No se encontraron datos para este horario.</span>
                </div>
            );
        }



        return (
            <div style={{ overflow: 'hidden', flex: 1, display: 'flex' }}>
                <div style={{ overflow: 'auto' }}>
                    {[...tables].reverse().map((tableData, tableIndex) => {
                        const checkpointCount = selectedSchedule.turns[0]?.length || 0;
                        const lapCount = tableData.rows.length || 0;
                        const tableColSpan = isTransposedView ? lapCount + 1 : (checkpointCount + 1) || 1;
                        const normalColumnTotals = selectedSchedule.turns[0]?.map((_, checkpointIndex) =>
                            getStatusTotals(tableData.rows.map(lap => lap[checkpointIndex]))
                        ) || [];
                        const transposedColumnTotals = tableData.rows.map(lap => getStatusTotals(lap));

                        return (
                            <TableContainer key={tableIndex} component={Paper} sx={{ backgroundColor: 'transparent', borderRadius: 'var(--card-border-radius)', marginBottom: '1rem' }}>
                                <Table className='schedule_table'>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell colSpan={tableColSpan} align="center" sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#ffff' }}>
                                                {selectedSchedule.name} - Tabla {tableIndex + 1} ({tableData.dates})
                                            </TableCell>
                                        </TableRow>
                                        {isTransposedView ? (
                                            <TableRow>
                                                <TableCell align="center" sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#ffff' }}>
                                                    Punto de control
                                                </TableCell>
                                                {tableData.rows.map((_, lapIndex) => (
                                                    <TableCell key={lapIndex} align="center" sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#ffff' }}>
                                                        Vuelta {lapIndex + 1}
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                        ) : (
                                            <TableRow>
                                                <TableCell align="center" sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#ffff' }}>
                                                    Vuelta
                                                </TableCell>
                                                {selectedSchedule.turns[0]?.map((point, pointIndex) => (
                                                    <TableCell key={pointIndex} align="center" sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#ffff' }}>
                                                        {point.label}
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                        )}
                                    </TableHead>
                                    <TableBody>
                                        {isTransposedView ? (
                                            selectedSchedule.turns[0]?.map((checkpoint, checkpointIndex) => (
                                                <TableRow key={checkpointIndex}>
                                                    <TableCell align="center" sx={{ fontSize: '0.7rem', border: '1px solid var(--color-dark-light)', fontWeight: 600 }}>
                                                        {checkpoint.label}
                                                    </TableCell>
                                                    {tableData.rows.map((lap, lapIndex) => (
                                                        <TableCell key={lapIndex} align="center" sx={{ fontSize: '0.7rem', border: '1px solid var(--color-dark-light)' }} {...getPointExportData(lap[checkpointIndex] || {})}>
                                                            {lap[checkpointIndex] ? renderPointCellContent(lap[checkpointIndex]) : ''}
                                                        </TableCell>
                                                    ))}
                                                </TableRow>
                                            )).concat(statusTotalRows.map((statusRow) => (
                                                <TableRow key={`total-${statusRow.status}`}>
                                                    <TableCell align="center" sx={{ fontSize: '0.7rem', border: '1px solid var(--color-dark-light)', fontWeight: 700, color: statusRow.color.css }} {...getStatusTotalExportData(statusRow.status)}>
                                                        {statusRow.label}
                                                    </TableCell>
                                                    {transposedColumnTotals.map((totals, lapIndex) => (
                                                        <TableCell key={lapIndex} align="center" sx={{ fontSize: '0.7rem', border: '1px solid var(--color-dark-light)', fontWeight: 700, color: statusRow.color.css }} {...getStatusTotalExportData(statusRow.status, totals[statusRow.status])}>
                                                            {renderStatusTotalContent(totals[statusRow.status])}
                                                        </TableCell>
                                                    ))}
                                                </TableRow>
                                            )))
                                        ) : (
                                            tableData.rows.map((lap, lapIndex) => (
                                                <TableRow key={lapIndex}>
                                                    <TableCell align="center" sx={{ fontSize: '0.7rem', border: '1px solid var(--color-dark-light)', fontWeight: 600 }}>
                                                        {lapIndex + 1}
                                                    </TableCell>
                                                    {lap.map((point, pointIndex) => (
                                                        <TableCell key={pointIndex} align="center" sx={{ fontSize: '0.7rem', border: '1px solid var(--color-dark-light)' }} {...getPointExportData(point)}>
                                                            {renderPointCellContent(point)}
                                                        </TableCell>
                                                    ))}
                                                </TableRow>
                                            )).concat(statusTotalRows.map((statusRow) => (
                                                <TableRow key={`total-${statusRow.status}`}>
                                                    <TableCell align="center" sx={{ fontSize: '0.7rem', border: '1px solid var(--color-dark-light)', fontWeight: 700, color: statusRow.color.css }} {...getStatusTotalExportData(statusRow.status)}>
                                                        {statusRow.label}
                                                    </TableCell>
                                                    {normalColumnTotals.map((totals, checkpointIndex) => (
                                                        <TableCell key={checkpointIndex} align="center" sx={{ fontSize: '0.7rem', border: '1px solid var(--color-dark-light)', fontWeight: 700, color: statusRow.color.css }} {...getStatusTotalExportData(statusRow.status, totals[statusRow.status])}>
                                                            {renderStatusTotalContent(totals[statusRow.status])}
                                                        </TableCell>
                                                    ))}
                                                </TableRow>
                                            )))
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        );
                    })}
                </div>
            </div>
        );
    };

    const getVehicleIdBySchedule = (scheduleName) => {
        return Object.entries(vehicleSchedules)
            .find(([_, configuredScheduleName]) =>
                configuredScheduleName === scheduleName
            )?.[0];
    };
    
    const selectedVehicleSchedules = Object.entries(vehicleSchedules)
        .map(([vehicleId, scheduleName]) => {
            const schedule = userSchedules.find(
                s => s.name === scheduleName
            );

            const vehicle = devices.find(
                d => String(d.id) === String(vehicleId)
            );

            if (!schedule || !vehicle) return null;

            return {
                ...schedule,
                configuredVehicleId: vehicle.id,
                configuredVehicleLabel: vehicle.label
            };
        })
        .filter(Boolean);

    return (
        <div className='tab_content_container'>
            <div className='filters_container' style={{ marginBottom: '0.5rem' }}>
                <span>Horario seleccionado:</span>
                <Select
                    id='scheduleViewSelecter'
                    value={
                        selectedSchedule?.name
                            ? `${selectedSchedule.name}-${getVehicleIdBySchedule(selectedSchedule.name) || ''}`
                            : ''
                    }
                    onChange={(e) => {

                        const schedule = selectedVehicleSchedules.find(
                            s =>
                                `${s.name}-${s.configuredVehicleId || s.vehicleId}`
                                === e.target.value
                        );

                        if (schedule) {
                            setSelectedSchedule(schedule);
                        }
                    }}
                    displayEmpty
                    sx={selectStyles}
                >
                    {selectedVehicleSchedules.map((schedule, index) => (

                        <MenuItem
                            key={`configured-schedule-${schedule.name}-${schedule.configuredVehicleId}-${index}`}
                            value={`${schedule.name}-${schedule.configuredVehicleId || schedule.vehicleId}`}
                            style={{ fontSize: '0.7rem' }}
                        >
                            {schedule.configuredVehicleLabel}
                            {' - '}
                            {schedule.name}
                        </MenuItem>
                    ))}
                </Select>
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginLeft: 'auto', gap: '0.5rem' }}>
                    <BootstrapTooltip title={isTransposedView ? "Ver por puntos de control" : "Ver por vueltas"}>
                        <Button
                            variant="outlined"
                            color="primary"
                            sx={{
                                padding: '0 0.8rem',
                                fontSize: '0.7rem',
                                borderRadius: 'var(--border-radius-1)',
                                transition: 'all 0.3s ease',
                                height: '2rem',
                                minWidth: 'auto'
                            }}
                            aria-label="Alternar visualización de la tabla"
                            onClick={() => setIsTransposedView(prev => !prev)}
                        >
                            <span className="material-symbols-rounded" style={{ verticalAlign: 'middle', fontSize: '1rem' }}>swap_horiz</span>
                        </Button>
                    </BootstrapTooltip>
                    <BootstrapTooltip title="Descargar">
                        <Button
                            variant="contained"
                            color="success"
                            sx={{
                                padding: '0 0.8rem',
                                fontSize: '0.7rem',
                                color: 'white',
                                borderRadius: 'var(--border-radius-1)',
                                transition: 'all 0.3s ease',
                                height: '2rem',
                                minWidth: 'auto'
                            }}
                            onClick={handleClickDownload}
                        >
                            <span className="material-symbols-rounded" style={{ verticalAlign: 'middle', fontSize: '1rem' }}>download</span>
                        </Button>
                    </BootstrapTooltip>
                    <Menu
                        anchorEl={anchorEl}
                        open={open}
                        onClose={handleClose}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                    >
                        <MenuItem
                            sx={{ fontSize: '0.8rem' }}
                            onClick={() => { handleExportPDF(); handleClose(); }}
                        >
                            Formato PDF
                        </MenuItem>
                        <MenuItem
                            sx={{ fontSize: '0.8rem' }}
                            onClick={() => { handleExportExcel(); handleClose(); }}
                        >
                            Formato XLSX
                        </MenuItem>
                    </Menu>
                </div>
            </div>
            {renderComparisonTable()}

        </div>
    )
}

export default ScheduleTab
